import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { projectService } from '../../../services/projectService';
import { leadService } from '../../../services/leadService';

const ProjectManagementPanel = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    budget: '',
    startDate: '',
    endDate: '',
    status: 'pending',
    clientId: '',
  });

  useEffect(() => {
    loadProjects();

    const unsubscribe = projectService?.subscribeToChanges((payload) => {
      // Real-time payloads are snake_case — reload to get proper joins
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE' || payload?.eventType === 'DELETE') {
        loadProjects();
      }
    });

    return () => unsubscribe();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const [data, clientsData] = await Promise.all([
        projectService?.getAll(),
        leadService?.getAll(),
      ]);
      setProjects(data);
      setClients(clientsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMilestones = async (projectId) => {
    try {
      const data = await projectService?.getMilestones(projectId);
      setMilestones(data);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await projectService?.updateStatus(projectId, newStatus);
      loadProjects();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleMilestoneStatusChange = async (milestoneId, newStatus) => {
    try {
      await projectService?.updateMilestone(milestoneId, { status: newStatus });
      if (selectedProject) {
        loadMilestones(selectedProject?.id);
      }
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleViewProject = async (project) => {
    setSelectedProject(project);
    await loadMilestones(project?.id);
  };

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project?.title || '',
        description: project?.description || '',
        budget: project?.budget || '',
        startDate: project?.startDate || '',
        endDate: project?.endDate || '',
        status: project?.status || 'pending',
        clientId: project?.clientId || '',
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        budget: '',
        startDate: '',
        endDate: '',
        status: 'pending',
        clientId: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      if (editingProject) {
        await projectService?.update(editingProject?.id, formData);
      } else {
        await projectService?.create(formData);
      }
      handleCloseModal();
      loadProjects();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet?')) return;

    try {
      await projectService?.delete(projectId);
      loadProjects();
    } catch (err) {
      setError(err?.message);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Tous les projets' },
    { value: 'pending', label: 'En attente' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'on_hold', label: 'En pause' },
    { value: 'completed', label: 'Terminés' },
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'var(--color-warning)',
      in_progress: 'var(--color-trust)',
      on_hold: 'var(--color-muted-foreground)',
      completed: 'var(--color-success)',
      cancelled: 'var(--color-error)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getMilestoneStatusColor = (status) => {
    const colors = {
      pending: 'var(--color-muted-foreground)',
      in_progress: 'var(--color-trust)',
      completed: 'var(--color-success)',
      delayed: 'var(--color-error)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const filteredProjects =
    selectedStatus === 'all' ? projects : projects?.filter((p) => p?.status === selectedStatus);

  if (loading && projects?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-muted-foreground">Chargement des projets...</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-xl shadow-premium border border-border">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">
                Gestion des Projets
              </h3>
              <p className="text-sm text-muted-foreground">
                Suivez l'avancement et les livrables de vos projets
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                className="w-40"
              />
              <Button 
                variant="default" 
                iconName="Plus" 
                iconPosition="left" 
                size="sm"
                onClick={() => handleOpenModal()}
              >
                Nouveau Projet
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-error/10 border-b border-error/20">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}

        <div className="p-4 md:p-6">
          <div className="space-y-4">
            {filteredProjects?.map((project) => (
              <div
                key={project?.id}
                className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${getStatusColor(project?.status)}15` }}
                    >
                      <Icon
                        name="Briefcase"
                        size={24}
                        style={{ color: getStatusColor(project?.status) }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                        {project?.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Icon name="Hash" size={12} />
                          {project?.projectNumber}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="User" size={12} />
                          {project?.leads?.name || 'Client'}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          {project?.startDate
                            ? new Date(project?.startDate)?.toLocaleDateString('fr-FR')
                            : 'Non défini'}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Progression</span>
                          <span className="text-xs font-medium text-foreground">
                            {project?.progressPercentage || 0}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${project?.progressPercentage || 0}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(project?.status)}15`,
                            color: getStatusColor(project?.status),
                          }}
                        >
                          {project?.status?.toUpperCase()?.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row items-start md:items-center gap-3 lg:gap-4">
                    <div className="text-left md:text-right">
                      <p className="text-lg font-bold text-foreground">
                        ${project?.budget?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-muted-foreground">Budget</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewProject(project)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                        title="Voir détails"
                      >
                        <Icon name="Eye" size={16} className="text-primary" />
                      </button>
                      <button
                        onClick={() => handleOpenModal(project)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                        title="Modifier"
                      >
                        <Icon name="Edit" size={16} className="text-primary" />
                      </button>
                      {project?.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(project?.id, 'in_progress')}
                          className="w-9 h-9 rounded-lg bg-background hover:bg-trust/10 flex items-center justify-center transition-colors"
                          title="Démarrer"
                        >
                          <Icon name="Play" size={16} className="text-trust" />
                        </button>
                      )}
                      {project?.status === 'in_progress' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(project?.id, 'on_hold')}
                            className="w-9 h-9 rounded-lg bg-background hover:bg-warning/10 flex items-center justify-center transition-colors"
                            title="Mettre en pause"
                          >
                            <Icon name="Pause" size={16} className="text-warning" />
                          </button>
                          <button
                            onClick={() => handleStatusChange(project?.id, 'completed')}
                            className="w-9 h-9 rounded-lg bg-background hover:bg-success/10 flex items-center justify-center transition-colors"
                            title="Terminer"
                          >
                            <Icon name="CheckCircle" size={16} className="text-success" />
                          </button>
                        </>
                      )}
                      {project?.status === 'on_hold' && (
                        <button
                          onClick={() => handleStatusChange(project?.id, 'in_progress')}
                          className="w-9 h-9 rounded-lg bg-background hover:bg-trust/10 flex items-center justify-center transition-colors"
                          title="Reprendre"
                        >
                          <Icon name="Play" size={16} className="text-trust" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteProject(project?.id)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-error/10 flex items-center justify-center transition-colors"
                        title="Supprimer"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredProjects?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Briefcase" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Aucun projet trouvé</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-foreground">{selectedProject?.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedProject?.projectNumber}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-foreground mb-4">Timeline du Projet</h4>
                <div className="space-y-4">
                  {milestones?.map((milestone, index) => (
                    <div key={milestone?.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: `${getMilestoneStatusColor(milestone?.status)}15`,
                          }}
                        >
                          <Icon
                            name={
                              milestone?.status === 'completed'
                                ? 'CheckCircle'
                                : milestone?.status === 'in_progress' ?'Clock' :'Circle'
                            }
                            size={20}
                            style={{ color: getMilestoneStatusColor(milestone?.status) }}
                          />
                        </div>
                        {index < milestones?.length - 1 && (
                          <div className="w-0.5 h-full bg-border mt-2" />
                        )}
                      </div>

                      <div className="flex-1 pb-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h5 className="font-semibold text-foreground mb-1">
                              {milestone?.title}
                            </h5>
                            <p className="text-sm text-muted-foreground mb-2">
                              {milestone?.description}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Icon name="Calendar" size={12} />
                                {milestone?.dueDate
                                  ? new Date(milestone?.dueDate)?.toLocaleDateString('fr-FR')
                                  : 'Non défini'}
                              </span>
                              {milestone?.deliverables?.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Icon name="Package" size={12} />
                                    {milestone?.deliverables?.length} livrables
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {milestone?.status === 'pending' && (
                              <button
                                onClick={() =>
                                  handleMilestoneStatusChange(milestone?.id, 'in_progress')
                                }
                                className="px-3 py-1 rounded-lg bg-trust/10 text-trust text-xs font-medium hover:bg-trust/20 transition-colors"
                              >
                                Démarrer
                              </button>
                            )}
                            {milestone?.status === 'in_progress' && (
                              <button
                                onClick={() =>
                                  handleMilestoneStatusChange(milestone?.id, 'completed')
                                }
                                className="px-3 py-1 rounded-lg bg-success/10 text-success text-xs font-medium hover:bg-success/20 transition-colors"
                              >
                                Terminer
                              </button>
                            )}
                          </div>
                        </div>

                        {milestone?.deliverables?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-foreground mb-2">Livrables:</p>
                            <ul className="space-y-1">
                              {milestone?.deliverables?.map((deliverable, idx) => (
                                <li
                                  key={idx}
                                  className="text-xs text-muted-foreground flex items-center gap-2"
                                >
                                  <Icon name="CheckSquare" size={12} />
                                  {deliverable}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {milestones?.length === 0 && (
                    <div className="text-center py-8">
                      <Icon name="Clock" size={32} className="text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Aucune étape définie</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingProject ? 'Modifier le Projet' : 'Nouveau Projet'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <Input
                label="Titre du projet *"
                value={formData?.title}
                onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Client</label>
                <select
                  value={formData?.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e?.target?.value })}
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground"
                >
                  <option value="">Sélectionnez un client (optionnel)</option>
                  {clients?.map((client) => (
                    <option key={client?.id} value={client?.id}>
                      {client?.name} - {client?.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Budget *"
                  type="number"
                  step="0.01"
                  value={formData?.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e?.target?.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Statut *</label>
                  <Select
                    options={statusOptions?.filter(opt => opt?.value !== 'all')}
                    value={formData?.status}
                    onChange={(value) => setFormData({ ...formData, status: value })}
                  />
                </div>
                <Input
                  label="Date de début"
                  type="date"
                  value={formData?.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                />
                <Input
                  label="Date de fin"
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingProject ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectManagementPanel;